// ============================================
// Garment Instructor — Tap-to-Instruct Panel
//
// Appears when an operator taps a garment piece
// in the Fashion Plate dressing room. The operator
// types an instruction, and the prompt maker
// processes it into nurse configuration changes.
// ============================================

import { useState, useCallback } from 'react';
import { T, F } from '../../quality/tokens';
import type {
  GarmentPiece,
  GarmentState,
  PromptMakerOutput,
  TapRegion,
} from '../../../types/fashionPlate';
import type { ContextLayer } from '../../../types/patientJourney';
import { runPromptMaker } from '../../../services/fashionPlate/promptMaker';

const PIECE_TO_TAP: Record<GarmentPiece, TapRegion> = {
  brain: 'brain',
  hat: 'brain',
  heart: 'heart',
  shoes: 'personality',
  accessories: 'personality',
  dress: 'dress',
};

interface GarmentInstructorProps {
  piece: GarmentPiece;
  currentGarments: GarmentState[];
  currentContext: Partial<ContextLayer>;
  onResult: (output: PromptMakerOutput) => void;
  onClose: () => void;
}

export function GarmentInstructor({
  piece,
  currentGarments,
  currentContext,
  onResult,
  onClose,
}: GarmentInstructorProps) {
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const canApply = instruction.trim().length > 0 && !loading && !confirmation;

  const handleApply = useCallback(async () => {
    if (!canApply) return;

    setLoading(true);
    setError(null);

    try {
      const result = await runPromptMaker({
        instruction: instruction.trim(),
        tapRegion: PIECE_TO_TAP[piece],
        currentGarments,
        currentContext,
      });

      // Show the nurse confirmation briefly before passing result up
      setLoading(false);
      setConfirmation(result.nurseConfirmation);

      setTimeout(() => {
        onResult(result);
      }, 1500);
    } catch (err) {
      setLoading(false);
      setError(
        err instanceof Error ? err.message : 'Something went wrong. Try again.',
      );
    }
  }, [canApply, instruction, piece, currentGarments, currentContext, onResult]);

  return (
    <div
      style={{
        padding: 16,
        background: T.bgCard,
        borderRadius: T.radius.lg,
        border: `1px solid ${T.border}`,
        boxShadow: T.shadowMd,
        marginTop: 8,
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontFamily: F.mono,
            fontSize: 10,
            fontWeight: 600,
            color: '#8B5CF6',
            textTransform: 'uppercase' as const,
            letterSpacing: 2,
          }}
        >
          INSTRUCT: {piece.toUpperCase()}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            padding: 4,
            cursor: 'pointer',
            fontFamily: F.body,
            fontSize: T.fontSize.sm,
            color: T.textMuted,
            lineHeight: 1,
          }}
        >
          x
        </button>
      </div>

      {/* Textarea */}
      <textarea
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        placeholder={`Tell the nurse how to adjust her ${piece}...`}
        disabled={loading || !!confirmation}
        style={{
          width: '100%',
          minHeight: 56,
          padding: 10,
          background: T.bgInput,
          border: `1px solid ${T.borderSoft}`,
          borderRadius: T.radius.md,
          fontFamily: F.body,
          fontSize: T.fontSize.sm,
          color: T.text,
          resize: 'vertical' as const,
          outline: 'none',
          boxSizing: 'border-box' as const,
          marginBottom: 10,
          opacity: loading || confirmation ? 0.6 : 1,
          transition: 'opacity 0.2s ease',
        }}
      />

      {/* Apply button */}
      <button
        onClick={handleApply}
        disabled={!canApply}
        style={{
          width: '100%',
          height: 40,
          borderRadius: T.radius.md,
          border: 'none',
          fontFamily: F.body,
          fontSize: T.fontSize.sm,
          fontWeight: 600,
          cursor: canApply ? 'pointer' : 'default',
          background: canApply
            ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
            : T.bgInput,
          color: canApply ? '#fff' : T.textGhost,
          transition: 'background 0.2s ease, color 0.2s ease',
        }}
      >
        {loading ? 'Updating...' : 'Apply'}
      </button>

      {/* Confirmation message */}
      {confirmation && (
        <p
          style={{
            marginTop: 10,
            marginBottom: 0,
            fontFamily: F.body,
            fontSize: T.fontSize.sm,
            fontStyle: 'italic',
            color: T.textSoft,
            lineHeight: 1.4,
            transition: 'opacity 0.3s ease',
          }}
        >
          {confirmation}
        </p>
      )}

      {/* Error message */}
      {error && (
        <p
          style={{
            marginTop: 10,
            marginBottom: 0,
            fontFamily: F.body,
            fontSize: T.fontSize.xs,
            color: T.danger,
            lineHeight: 1.4,
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
